import type { FC, PropsWithChildren } from "react";

import { classes, Icon, Text } from "@alpacahq/ui/old";

import { FullScreenSpinner } from "~/v2/components/common";
import { Footer } from "~/v2/components/common/footer";
import { useAccountContext } from "~/v2/providers/AccountProvider";
import App from "../../index";
import { STEPS, type Step } from "./steps";
import { useIsOptionsOnboardingAvailable } from "./util";

/**
 * @todo: TECH DEBT
 *
 * This is basically the same component used in Entity Onboarding (copy changes to title, this uses UI instead of Chakra).
 * This should be moved to a shared location at a later time when both features are deployed, and refactored so both use this version.
 */

type LayoutProps = {
  title?: string;
  description?: string;
  currentStep: Step;
  isFooterDisplayed?: boolean;
};

export const Layout: FC<PropsWithChildren<LayoutProps>> = ({
  title = "Apply for Options Trading",
  description,
  currentStep,
  children,
  isFooterDisplayed,
}) => {
  const { isLoading: isAccountLoading } = useAccountContext({ liveOnly: true });
  const {
    isLoading: isApprovalLoading,
    isOptionsOnboardingAvailable: showOptionOnboarding,
  } = useIsOptionsOnboardingAvailable();

  const isLoading = isAccountLoading || isApprovalLoading;

  return (
    <App>
      <div className="pb-16">
        {isLoading ? (
          <div className="min-h-screen">
            <FullScreenSpinner />
          </div>
        ) : (
          <div>
            <div className="pr-8 pl-8 md:pl-0">
              <h1 className="font-bold text-3xl">{title}</h1>
              <Text className="text-gray-700 text-lg">{description}</Text>
            </div>
            <hr className="mt-5 mb-7" />
            <div className="mx-auto flex h-full flex-col pr-8 pl-8 min-[900px]:flex-row min-[900px]:pl-0">
              {showOptionOnboarding ? (
                <>
                  <div className="mr-8">
                    <Progress
                      reached={
                        STEPS.findIndex((s) => s.key === currentStep.key) + 1
                      }
                      steps={STEPS}
                    />
                  </div>
                  <div className="grow">{children}</div>
                </>
              ) : (
                <div>Not Available.</div>
              )}
            </div>
          </div>
        )}
      </div>
      {isFooterDisplayed && <Footer legal={true} />}
    </App>
  );
};

interface ProgressProps {
  steps: Step[];
  reached: number;
}

const Progress = ({ reached, steps }: ProgressProps) => (
  <div className="sticky top-0 mx-auto mb-4 flex min-w-72 flex-column">
    <div>
      {steps.map(({ title }, i) => (
        <Milestone
          key={title}
          title={title}
          highlighted={i + 1 === reached}
          reached={i + 1 < reached}
          value={i + 1}
          marginBottom={i + 1 < steps.length ? "mb-4" : "mb-0"}
        />
      ))}
    </div>
  </div>
);

interface MilestoneProps {
  reached: boolean;
  highlighted: boolean;
  value: number;
  title: string;
  marginBottom?: string;
}

const Milestone = ({
  highlighted,
  title,
  reached,
  value,
  marginBottom = "",
}: MilestoneProps) => (
  <div className={classes("flex items-center", marginBottom)}>
    <div
      className={classes(
        "flex h-10 w-10 items-center justify-center rounded-lg align-center font-bold text-sm",
        reached && !highlighted ? "text-yellow-100" : "text-black",
        !reached && !highlighted ? "bg-yellow-100" : "bg-yellow-300",
      )}
    >
      {reached ? <Icon name="Check" className="text-black" /> : value}
    </div>
    <div className="ml-4 font-bold text-sm">
      <Text>{title}</Text>
    </div>
  </div>
);
